import AcademyPortalAccess from './AcademyPortalAccess';
import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, DetailField, fmtMoney } from './AcademyShared';

const STATUSES = ['active', 'inactive', 'archived'];
const SCOPE_TYPES = ['default', 'category', 'bootcamp_type', 'cohort', 'level'];
const COMP_TYPES = ['fixed', 'per_session', 'per_hour', 'percentage'];

export default function AcademyInstructors() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/academy/instructors?per_page=50`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const changeStatus = async (id, newStatus) => {
    await fetch(`${API}/companies/${companyId}/academy/instructors/${id}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    fetchList();
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this instructor?')) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/instructors/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>👨‍🏫 Instructors</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Instructor</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input style={{ ...inputStyle, width: 220 }} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...inputStyle, width: 160 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Code', 'Name', 'Email', 'Phone', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No instructors</td></tr>}
              {list.map(i => (
                <tr key={i.id}>
                  <td style={{ ...tdStyle, fontWeight: 600, cursor: 'pointer', color: '#3b82f6' }} onClick={() => setViewing(i)}>{i.instructor_code}</td>
                  <td style={tdStyle}>{i.display_name}</td>
                  <td style={tdStyle}>{i.email}</td>
                  <td style={tdStyle}>{i.phone || '—'}</td>
                  <td style={tdStyle}><Badge value={i.status} /></td>
                  <td style={tdStyle}>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setViewing(i)}>View</button>
                    <button style={{ ...btnSmall, marginRight: 6, color: '#2563eb' }} onClick={() => setViewing({ ...i, startContract: true })}>+ Contract</button>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setEditing(i)}>Edit</button>
                    {i.status === 'active' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(i.id, 'inactive')}>Deactivate</button>}
                    {i.status !== 'active' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(i.id, 'active')}>Activate</button>}
                    <button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeItem(i.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <InstructorModal companyId={companyId} item={editing} onClose={() => setEditing(null)} onSaved={record => { setEditing(null); fetchList(); if (record?.id) setViewing(record); }} />}
      {viewing && <InstructorDetail companyId={companyId} instructorId={viewing.id} startContract={viewing.startContract} portalInvitationError={viewing.portal_invitation_error} onClose={() => setViewing(null)} />}
    </div>
  );
}

function InstructorModal({ companyId, item, onClose, onSaved }) {
  const isNew = !item.id;
  const [creationId] = useState(() => window.crypto.randomUUID());
  const [form, setForm] = useState({
    display_name: item.display_name || '', email: item.email || '', phone: item.phone || '', notes: item.notes || '',
    specializations: Array.isArray(item.specializations) ? item.specializations.join(', ') : '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.display_name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/companies/${companyId}/academy/instructors` : `${API}/companies/${companyId}/academy/instructors/${item.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, ...(isNew ? { portal_creation_id: creationId } : {}), specializations: form.specializations.split(',').map(value => value.trim()).filter(Boolean) }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      const record = await res.json();
      onSaved(record);
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{isNew ? 'New Instructor' : 'Edit Instructor'}</h3>
        <FormRow label="Name *"><input style={inputStyle} value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} /></FormRow>
        <FormRow label="Email"><input style={inputStyle} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></FormRow>
        <FormRow label="Phone"><input style={inputStyle} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></FormRow>
        <FormRow label="Specialties"><input style={inputStyle} value={form.specializations} onChange={e => setForm({ ...form, specializations: e.target.value })} placeholder="For example: UI/UX, Graphic Design" /><small>Separate specialties with commas.</small></FormRow>
        <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 60 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

export function InstructorDetail({ companyId, instructorId, startContract = false, portalInvitationError, onClose }) {
  const [instructor, setInstructor] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [addingProfile, setAddingProfile] = useState(false);
  const [addingContract, setAddingContract] = useState(startContract);
  const [categories, setCategories] = useState([]);
  const [bootcampTypes, setBootcampTypes] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [profileForm, setProfileForm] = useState({ scope_type: 'default', scope_id: '', compensation_type: 'fixed', rate: '' });
  const [contractForm, setContractForm] = useState({ cohort_id: '', title: '', compensation_type: 'fixed', agreed_amount: '', contract_date: new Date().toISOString().slice(0, 10), notes: '' });
  const [paymentForms, setPaymentForms] = useState({});

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/academy/instructors/${instructorId}`, { headers: getAuthHeaders() });
      const j = await res.json();
      setInstructor(j.instructor ? { ...j.instructor, totals: j.totals } : j);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId, instructorId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  useEffect(() => {
    fetch(`${API}/companies/${companyId}/academy/categories?per_page=200&status=active`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : { data: [] }).then(j => setCategories(Array.isArray(j.data) ? j.data : []));
    fetch(`${API}/companies/${companyId}/academy/bootcamp-types?per_page=200&status=active`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : { data: [] }).then(j => setBootcampTypes(Array.isArray(j.data) ? j.data : []));
    fetch(`${API}/companies/${companyId}/academy/cohorts?per_page=200`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : { data: [] }).then(j => setCohorts(Array.isArray(j.data) ? j.data : []));
  }, [companyId]);

  const scopeOptions = () => {
    if (profileForm.scope_type === 'category') return categories;
    if (profileForm.scope_type === 'bootcamp_type') return bootcampTypes;
    if (profileForm.scope_type === 'cohort') return cohorts;
    return [];
  };

  const addProfile = async () => {
    if (!profileForm.rate) { alert('Amount is required'); return; }
    const scopeKeyMap = { category: 'category_id', bootcamp_type: 'bootcamp_type_id', cohort: 'cohort_id', level: 'level_id' };
    const body = { scope_type: profileForm.scope_type, compensation_type: profileForm.compensation_type, rate: profileForm.rate };
    if (profileForm.scope_type !== 'default') body[scopeKeyMap[profileForm.scope_type]] = profileForm.scope_id;
    const res = await fetch(`${API}/companies/${companyId}/academy/instructors/${instructorId}/compensation-profiles`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Failed to add profile'); return; }
    setAddingProfile(false);
    setProfileForm({ scope_type: 'default', scope_id: '', compensation_type: 'fixed', rate: '' });
    fetchDetail();
  };

  const removeProfile = async (profileId) => {
    if (!window.confirm('Delete this compensation profile?')) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/instructors/${instructorId}/compensation-profiles/${profileId}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchDetail();
  };

  const addContract = async () => {
    if (!contractForm.cohort_id || contractForm.agreed_amount === '') { alert('Cohort and agreed amount are required.'); return; }
    const response = await fetch(`${API}/companies/${companyId}/academy/instructors/${instructorId}/contracts`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(contractForm),
    });
    if (!response.ok) { const result = await response.json().catch(() => ({})); alert(result.message || 'Could not create the contract.'); return; }
    setAddingContract(false); setContractForm({ cohort_id: '', title: '', compensation_type: 'fixed', agreed_amount: '', contract_date: new Date().toISOString().slice(0, 10), notes: '' }); fetchDetail();
  };

  const addContractPayment = async (contractId) => {
    const form = paymentForms[contractId] || {};
    if (!form.amount || Number(form.amount) <= 0) { alert('Enter a payment amount.'); return; }
    const response = await fetch(`${API}/companies/${companyId}/academy/instructors/${instructorId}/contracts/${contractId}/payments`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form),
    });
    if (!response.ok) { const result = await response.json().catch(() => ({})); alert(result.message || 'Could not add the payment.'); return; }
    setPaymentForms(forms => ({ ...forms, [contractId]: { amount: '', date: '', notes: '' } })); fetchDetail();
  };

  const setContractPaymentStatus = async (contractId, paymentId, status) => {
    const response = await fetch(`${API}/companies/${companyId}/academy/instructors/${instructorId}/contracts/${contractId}/payments/${paymentId}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
    });
    if (!response.ok) { const result = await response.json().catch(() => ({})); alert(result.message || 'Could not update the payment.'); return; }
    fetchDetail();
  };

  const removeContract = async (contractId) => {
    if (!window.confirm('Delete this contract and its payment schedule?')) return;
    const response = await fetch(`${API}/companies/${companyId}/academy/instructors/${instructorId}/contracts/${contractId}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!response.ok) { const result = await response.json().catch(() => ({})); alert(result.message || 'Could not delete the contract.'); return; }
    fetchDetail();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 940, maxWidth: '95vw' }} onClick={e => e.stopPropagation()}>
        {initialLoading || !instructor ? <div>Loading...</div> : (
          <>
            <h3 style={{ marginTop: 0 }}>{instructor.display_name} <Badge value={instructor.status} /></h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
              <DetailField label="Code" value={instructor.instructor_code} />
              <DetailField label="Email" value={instructor.email} />
              <DetailField label="Phone" value={instructor.phone} />
            </div>

            {portalInvitationError && <p role="alert" style={{ color: '#b42318' }}>Instructor saved. {portalInvitationError}</p>}
            <AcademyPortalAccess role="instructor" entityId={instructorId} companyId={companyId} />
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h4 style={{ margin: 0 }}>Contracts</h4>
                <button style={btnSmall} onClick={() => setAddingContract(value => !value)}>{addingContract ? 'Cancel' : '+ Add Contract'}</button>
              </div>
              {addingContract && <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <FormRow label="Cohort *"><select style={inputStyle} value={contractForm.cohort_id} onChange={e => setContractForm({ ...contractForm, cohort_id: e.target.value })}><option value="">Select cohort</option>{cohorts.map(cohort => <option key={cohort.id} value={cohort.id}>{cohort.cohort_code} — {cohort.name}</option>)}</select></FormRow>
                  <FormRow label="Contract title"><input style={inputStyle} placeholder="For example, UI/UX instructor agreement" value={contractForm.title} onChange={e => setContractForm({ ...contractForm, title: e.target.value })} /></FormRow>
                  <FormRow label="Compensation type"><select style={inputStyle} value={contractForm.compensation_type} onChange={e => setContractForm({ ...contractForm, compensation_type: e.target.value })}><option value="fixed">Fixed amount</option><option value="per_session">Per session</option></select></FormRow>
                  <FormRow label={contractForm.compensation_type === 'per_session' ? 'Rate per session *' : 'Agreed amount *'}><input type="number" min="0" step="0.01" style={inputStyle} value={contractForm.agreed_amount} onChange={e => setContractForm({ ...contractForm, agreed_amount: e.target.value })} /></FormRow>
                  <FormRow label="Contract date"><input type="date" style={inputStyle} value={contractForm.contract_date} onChange={e => setContractForm({ ...contractForm, contract_date: e.target.value })} /></FormRow>
                </div>
                <FormRow label="Contract details / notes"><textarea style={{ ...inputStyle, minHeight: 54 }} value={contractForm.notes} onChange={e => setContractForm({ ...contractForm, notes: e.target.value })} /></FormRow>
                <button style={btnPrimary} onClick={addContract}>Save Contract</button>
              </div>}
              {(instructor.contracts || []).length === 0 && <div style={{ color: '#9ca3af', fontSize: 13, padding: '8px 0' }}>No contracts yet.</div>}
              {(instructor.contracts || []).map(contract => {
                const payments = contract.payments || []; const paid = payments.filter(payment => payment.status === 'paid').reduce((sum, payment) => sum + Number(payment.amount || 0), 0); const form = paymentForms[contract.id] || { amount: '', date: '', notes: '' };
                return <div key={contract.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginTop: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}><div>{contract.title && <strong>{contract.title}</strong>}<div style={{ color: '#64748b', fontSize: 13, marginTop: 3 }}>{contract.cohort?.cohort_code} — {contract.cohort?.name} · {contract.compensation_type === 'per_session' ? `Per session ${fmtMoney(contract.agreed_amount)}` : `Fixed ${fmtMoney(contract.agreed_amount)}`} · Paid {fmtMoney(paid)}</div>{contract.notes && <div style={{ fontSize: 13, marginTop: 4 }}>{contract.notes}</div>}</div><button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeContract(contract.id)}>Delete</button></div>
                  <table style={{ ...tableStyle, marginTop: 10 }}><thead><tr>{['Payment', 'Date', 'Status', 'Action'].map(header => <th key={header} style={thStyle}>{header}</th>)}</tr></thead><tbody>{payments.length === 0 && <tr><td colSpan={4} style={{ ...tdStyle, color: '#9ca3af' }}>{contract.compensation_type === 'per_session' ? 'Payments appear automatically when sessions are completed.' : 'No payments recorded yet.'}</td></tr>}{payments.map(payment => <tr key={payment.id}><td style={tdStyle}>{fmtMoney(payment.amount)}{payment.session && <div style={{ fontSize: 11, color: '#64748b' }}>{payment.session.title}</div>}</td><td style={tdStyle}>{payment.due_date || '—'}</td><td style={tdStyle}><Badge value={payment.status} /></td><td style={tdStyle}><button style={btnSmall} onClick={() => setContractPaymentStatus(contract.id, payment.id, payment.status === 'paid' ? 'pending' : 'paid')}>{payment.status === 'paid' ? 'Mark Pending' : 'Mark Paid'}</button></td></tr>)}</tbody></table>
                  {contract.compensation_type === 'per_session' ? <div style={{ color: '#64748b', fontSize: 12, marginTop: 10 }}>A pending payment is added automatically for each completed session taught in this cohort.</div> : <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginTop: 10 }}><input type="number" min="0.01" step="0.01" style={inputStyle} placeholder="Payment amount" value={form.amount} onChange={e => setPaymentForms(forms => ({ ...forms, [contract.id]: { ...form, amount: e.target.value } }))} /><input type="date" style={inputStyle} value={form.date || ''} onChange={e => setPaymentForms(forms => ({ ...forms, [contract.id]: { ...form, date: e.target.value } }))} /><button style={btnSmall} onClick={() => addContractPayment(contract.id)}>+ Add Payment</button></div>}
                </div>;
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h4 style={{ margin: 0 }}>Compensation Profiles <span style={{ fontSize: 11, color: '#9ca3af' }}>(priority: Level &gt; Cohort &gt; Bootcamp Type &gt; Category &gt; Default)</span></h4>
              <button style={btnSmall} onClick={() => setAddingProfile(v => !v)}>{addingProfile ? 'Cancel' : '+ Add Profile'}</button>
            </div>
            {addingProfile && (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <FormRow label="Scope">
                    <select style={inputStyle} value={profileForm.scope_type} onChange={e => setProfileForm({ ...profileForm, scope_type: e.target.value, scope_id: '' })}>
                      {SCOPE_TYPES.filter(s => s !== 'level').map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </FormRow>
                  {profileForm.scope_type !== 'default' && (
                    <FormRow label={profileForm.scope_type.replace('_', ' ')}>
                      <select style={inputStyle} value={profileForm.scope_id} onChange={e => setProfileForm({ ...profileForm, scope_id: e.target.value })}>
                        <option value="">Select...</option>
                        {scopeOptions().map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                      </select>
                    </FormRow>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <FormRow label="Compensation Type">
                    <select style={inputStyle} value={profileForm.compensation_type} onChange={e => setProfileForm({ ...profileForm, compensation_type: e.target.value })}>
                      {COMP_TYPES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                    </select>
                  </FormRow>
                  <FormRow label="Amount">
                    <input type="number" step="0.01" style={inputStyle} value={profileForm.rate} onChange={e => setProfileForm({ ...profileForm, rate: e.target.value })} />
                  </FormRow>
                </div>
                <button style={btnPrimary} onClick={addProfile}>Save Profile</button>
              </div>
            )}
            <table style={tableStyle}>
              <thead><tr>{['Scope', 'Cohort', 'Type', 'Amount', 'Payment Status', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {(instructor.compensation_profiles || []).length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No profiles</td></tr>}
                {(instructor.compensation_profiles || []).map(p => (
                  <tr key={p.id}>
                    <td style={tdStyle}>{p.scope_type}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'normal' }}>{p.cohort_names?.length ? p.cohort_names.join(', ') : p.cohort?.name || (p.scope_type === 'cohort' || p.scope_type === 'level' ? 'Not assigned' : 'Multiple / applicable cohorts')}</td>
                    <td style={tdStyle}>{p.compensation_type}</td>
                    <td style={tdStyle}>{p.compensation_type === 'percentage' ? `${p.rate}%` : fmtMoney(p.rate)}</td>
                    <td style={tdStyle}><Badge value={p.payment_summary?.status || 'no_payment_recorded'} />
                      {p.payment_summary && <div style={{ fontSize: 11, marginTop: 4 }}>Paid {fmtMoney(p.payment_summary.paid_amount)} · Remaining {fmtMoney(p.payment_summary.outstanding_amount)}</div>}
                    </td>
                    <td style={tdStyle}><button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeProfile(p.id)}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={btnSecondary} onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
